import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useContext,
    useMemo,
    useState,
} from 'react';
import { usePagination } from '../hooks/usePagination';
import { useDeviceOperations } from '../hooks/useDeviceOperations';
import { useDeviceFilters } from '../hooks/useDeviceFilters';

type TProps = {
    children: ReactNode;
};

export type TDeviceModel = {
    id: number;
    manufacturer: {
        id: number;
        name: string;
    };
    name: string;
    protocol: {
        id: number;
        name: string;
    };
};

type TContext = {
    selectedDevice: TDevice | null;
    limit: number;
    offset: number;
    devicesList: TDevicesList;
    searchValue: string;
    selectedModels: string[];
    selectedManufacturers: string[];
    selectedStatuses: string[];
    deviceModels: TDeviceModel[];
    deviceManufacturers: any[];
    deviceDetail: TDevice | null;
    deviceLoading: boolean;
    alertMsgs: Partial<Record<string, string[]>>;
    deviceEvents: TDeviceEvents[];
    deviceMessages: TDeviceMessages[];
    connectionDetails: TConnectionDetails;
    setDeviceDetail:Dispatch<SetStateAction<TDevice | null>>
    setSearchValue: Dispatch<SetStateAction<string>>;
    setSelectedModels: Dispatch<SetStateAction<string[]>>;
    setSelectedManufactureres: Dispatch<SetStateAction<string[]>>;
    setSelectedStatus: Dispatch<SetStateAction<string[]>>;
    setOffset: Dispatch<SetStateAction<number>>;
    setLimit: Dispatch<SetStateAction<number>>;
    setAlertMessages: Dispatch<SetStateAction<Partial<Record<string, string[]>>>>
    setSelectedDevice: Dispatch<SetStateAction<TDevice | null>>;
    getDeviceList: (options?: { search?: string }) => Promise<void>;
    getDeviceDetail: (id: number) => void;
    getDeviceModels: () => void;
    getDeviceManufacturers: () => void;
    CreateDevice: (data: TCreateDeviceProp) => void;
    EditDevice: (id: number, data: TCreateDeviceProp) => void;
    pingDevice: (id: number) => void;
    deactivateDevice: (id: number) => void;
    activateDevice: (id: number) => void;
    deleteteDevice: (id: number) => void;
    getDevciceLatestEvents: (id: number) => void;
    getDeviceLatestMessages: (id: number) => void;
    getAllDeviceMessages: (id: number, offset: number) => Promise<TDeviceAllMessages>;
    getDeviceConnection: (id: number) => void;
    updateConnectionSettins: (id: number, params: TConnectionDetails) => Promise<TConnectionDetails | null>;
};

export type TDevice = {
    id: number;
    name: string;
    model: string;
    manufacturer: string;
    department: string;
    created_stamp: string;
    status: string;
    last_health_check: string;
    config: {
        id: number;
        description: string;
        applied_at: Date;
        version: number;
        protocol: string;
        ip_address: string;
        port: number;
    };
};

export type TDeviceEvents = {
    id: number;
    event_type: string;
    actor: string;
    created_at: string;
};

export type TDeviceMessages = {
    created_stamp: string;
    error_code: string;
    error_details: string;
    finished_at: Date;
    id: number;
    message_type: string;
    status: string;
};

export type TDeviceAllMessages = {
    count: number;
    next?: string | null;
    previous?: string | null;
    results: TDeviceMessages[];
};

export type TDevicesList = {
    count: number;
    next?: string | null;
    previous?: string | null;
    results: TDevice[];
    last_updated_stamp?: Date;
};

export interface TConnectionDetails {
    connection_name: string;
    ip_address: string;
    port: number;
    connection_timeout: number;
    read_timeout: number;
    retries: number;
    backoff_strategy: string;
    keep_alive_interval: number;
    failure_threshold: number;
    description: string;
    auth_username: string;
    auth_password: string;
}

export type TCreateDeviceProp = {
    model: number | string;
    name: string;
    location: string;
    config: {
        ip_address: string;
        port: number;
        description?: string;
        kiro_device_type?: number;
        kiro_device_id?: number;
    };
};

const DeviceContext = createContext<TContext>({} as TContext);

export const DeviceProvider = ({ children }: TProps) => {
    const [selectedDevice, setSelectedDevice] = useState<TDevice | null>(null);
    const [alertMsgs, setAlertMessages] = useState<Partial<Record<string, string[]>>>({});
    const { limit, offset, setLimit, setOffset } = usePagination(10);
    const {
        searchValue,
        selectedModels,
        selectedManufacturers,
        selectedStatuses,
        setSearchValue,
        setSelectedModels,
        setSelectedManufacturers: setSelectedManufactureres,
        setSelectedStatuses: setSelectedStatus,
    } = useDeviceFilters();

    const {
        devicesList,
        deviceDetail,
        deviceEvents,
        deviceMessages,
        connectionDetails,
        deviceModels,
        deviceManufacturers,
        deviceLoading,
        getDeviceList: fetchDeviceList,
        getDeviceDetail: fetchDeviceDetail,
        createDevice: createDeviceOperation,
        editDevice: editDeviceOperation,
        pingDevice: pingDeviceOperation,
        deactivateDevice: deactivateDeviceOperation,
        activateDevice: activateDeviceOperation,
        deleteDevice: deleteDeviceOperation,
        getDeviceLatestEvents: fetchDeviceLatestEvents,
        getDeviceLatestMessages: fetchDeviceLatestMessages,
        GetAllDeviceMessages: fetchAllDeviceMessages,
        getDeviceConnection: fetchDeviceConnection,
        updateConnectionSettings: updateConnectionSettingsOperation,
        getDeviceModels: fetchDeviceModels,
        getDeviceManufacturers: fetchDeviceManufacturers,
        setDeviceDetail
    } = useDeviceOperations();

    const CreateDevice = async (data: TCreateDeviceProp) => {
        await createDeviceOperation(data, setAlertMessages);
    };

    const EditDevice = async (id: number, data: TCreateDeviceProp) => {
        await editDeviceOperation(id, data, setAlertMessages);
    };

    const getDeviceList = async ({ search }: { search?: string } = {}) => {
        await fetchDeviceList(
            {
                search: search || searchValue,
                manufacturers: selectedManufacturers,
                models: selectedModels,
                statuses: selectedStatuses,
            },
            limit,
            offset,
            setAlertMessages
        );
    };

    const getDeviceModels = async () => {
        await fetchDeviceModels(setAlertMessages);
    };

    const getDeviceManufacturers = async () => {
        await fetchDeviceManufacturers(setAlertMessages);
    };

    const getDeviceDetail = async (id: number) => {
        await fetchDeviceDetail(id, setAlertMessages);
    };

    const pingDevice = async (id: number) => {
        await pingDeviceOperation(id, setAlertMessages);
    };

    const deactivateDevice = async (id: number) => {
        await deactivateDeviceOperation(id, setAlertMessages);
    };

    const activateDevice = async (id: number) => {
        await activateDeviceOperation(id, setAlertMessages);
    };

    const deleteteDevice = async (id: number) => {
        await deleteDeviceOperation(id, setAlertMessages);
    };

    const getDevciceLatestEvents = async (id: number) => {
        await fetchDeviceLatestEvents(id, setAlertMessages);
    };

    const getDeviceLatestMessages = async (id: number) => {
        await fetchDeviceLatestMessages(id, setAlertMessages);
    };

    const getAllDeviceMessages = async (id: number, offset: number) => {
        return await fetchAllDeviceMessages(id, offset, setAlertMessages);
    };

    const getDeviceConnection = async (id: number) => {
        await fetchDeviceConnection(id, setAlertMessages);
    };

    const updateConnectionSettins = async (
        id: number,
        params: TConnectionDetails
    ): Promise<TConnectionDetails | null> => {
        return await updateConnectionSettingsOperation(id, params, setAlertMessages);
    };

    const contextValue = useMemo(
        () => ({
            devicesList,
            selectedDevice,
            limit,
            offset,
            alertMsgs,
            deviceModels,
            searchValue,
            selectedModels,
            selectedManufacturers,
            selectedStatuses,
            deviceManufacturers,
            deviceDetail,
            deviceEvents,
            deviceMessages,
            connectionDetails,
            deviceLoading,
            setDeviceDetail,
            setAlertMessages,
            deleteteDevice,
            EditDevice,
            updateConnectionSettins,
            getDeviceConnection,
            CreateDevice,
            getDeviceDetail,
            getDevciceLatestEvents,
            getDeviceLatestMessages,
            getAllDeviceMessages,
            pingDevice,
            deactivateDevice,
            activateDevice,
            getDeviceList,
            getDeviceManufacturers,
            getDeviceModels,
            setSearchValue,
            setSelectedModels,
            setSelectedManufactureres,
            setSelectedStatus,
            setOffset,
            setLimit,
            setSelectedDevice,
        }),
        [
            devicesList,
            selectedDevice,
            deviceEvents,
            deviceMessages,
            deviceDetail,
            alertMsgs,
            offset,
            limit,
            deviceLoading,
            deviceManufacturers,
            deviceModels,
            searchValue,
            selectedModels,
            selectedManufacturers,
            selectedStatuses,
            connectionDetails,
        ]
    );

    return <DeviceContext.Provider value={contextValue}>{children}</DeviceContext.Provider>;
};

export const useDevices = () => {
    return useContext(DeviceContext);
};
